import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IDropdown } from '../../../models/models';
import { BranchZipcodeService } from '../../../services/admin/branch-zipcode.service';

export interface BranchZipResolution {
	branchId: number;
	branchName: string;
}

export type BranchZipLookupState = 'idle' | 'loading' | 'matched' | 'no-access' | 'no-match' | 'error';

interface BranchZipLookupPayload {
	branch_id: number;
	branch_name: string;
}

interface BranchZipLookupResult {
	zipcode: string;
	state: BranchZipLookupState;
	branchId: number | null;
	branchName: string;
	payload: BranchZipLookupPayload | null;
}

/**
 * Throwaway ZIP -> branch lookup that sits above a Branch dropdown. It never writes to
 * the form itself: it emits the resolved branch and the host page decides what to set,
 * and the ZIP typed here is never part of any payload.
 */
@Component({
	selector: 'app-branch-zip-lookup',
	templateUrl: './branch-zip-lookup.component.html',
	styleUrl: './branch-zip-lookup.component.scss'
})
export class BranchZipLookupComponent implements OnInit, OnDestroy {
	/** The branches the current user may pick — used to tell "no branch" apart from "no access". */
	@Input() branches$!: Observable<IDropdown[]>;
	@Input() disabled = false;

	/** Only fires for a branch the user can actually select. Never fires on miss/no-access/error. */
	@Output() branchResolved = new EventEmitter<BranchZipResolution | null>();

	zipcode = '';
	state: BranchZipLookupState = 'idle';
	queriedZipcode = '';
	branchName = '';

	private accessibleBranches: IDropdown[] = [];
	private lastPayload: BranchZipLookupPayload | null = null;
	private zipInput$ = new Subject<string>();
	private destroy$ = new Subject<void>();

	constructor(private branchZipcodeService: BranchZipcodeService) {}

	ngOnInit(): void {
		this.branches$?.pipe(takeUntil(this.destroy$)).subscribe((branches) => {
			this.accessibleBranches = branches ?? [];
			this.recheckAccess();
		});

		// distinctUntilChanged sits before debounceTime on purpose: debouncing first can
		// collapse "78701" -> "7870" -> "78701" into a single repeated emission, which
		// distinctUntilChanged would then swallow and leave the panel stuck on its last state.
		this.zipInput$
			.pipe(
				distinctUntilChanged(),
				debounceTime(300),
				tap((zipcode) => {
					if (zipcode.length === 5) {
						this.queriedZipcode = zipcode;
						this.state = 'loading';
					}
				}),
				switchMap((zipcode) => {
					if (zipcode.length !== 5) {
						return of<BranchZipLookupResult>({
							zipcode,
							state: 'idle',
							branchId: null,
							branchName: '',
							payload: null
						});
					}

					return this.branchZipcodeService.resolveBranchByZipcode(zipcode).pipe(
						map((res) => this.toResult(zipcode, res?.data ?? null)),
						catchError(() =>
							of<BranchZipLookupResult>({
								zipcode,
								state: 'error',
								branchId: null,
								branchName: '',
								payload: null
							})
						)
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((result) => {
				// A response for a ZIP the user has since edited must not overwrite the panel.
				if (result.zipcode !== this.zipcode) {
					return;
				}

				this.state = result.state;
				this.branchName = result.branchName;
				this.queriedZipcode = result.zipcode;
				this.lastPayload = result.payload;

				if (result.state === 'matched' && result.branchId !== null) {
					this.branchResolved.emit({ branchId: result.branchId, branchName: result.branchName });
				}
			});
	}

	onZipInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitized = (input.value || '').replace(/\D/g, '').slice(0, 5);

		// Keep the field showing exactly what we query — a pasted "78701-1234" truncates to "78701".
		if (input.value !== sanitized) {
			input.value = sanitized;
		}

		if (sanitized === this.zipcode) {
			return;
		}

		this.zipcode = sanitized;

		if (sanitized.length < 5) {
			this.state = 'idle';
			this.queriedZipcode = '';
			this.branchName = '';
		} else {
			this.queriedZipcode = sanitized;
			this.state = 'loading';
		}

		this.zipInput$.next(sanitized);
	}

	get resultMessage(): string {
		switch (this.state) {
			case 'loading':
				return `Looking up ${this.queriedZipcode}…`;
			case 'matched':
				return `${this.branchName} services ${this.queriedZipcode}. Branch set below.`;
			case 'no-access':
				return `${this.queriedZipcode} belongs to a branch you don't have access to. Choose a branch manually.`;
			case 'no-match':
				return `No branch services ${this.queriedZipcode}. Choose a branch manually.`;
			case 'error':
				return `Couldn't look up ${this.queriedZipcode}. Try again.`;
			default:
				return 'Enter a 5-digit ZIP to find the servicing branch.';
		}
	}

	get resultIcon(): string {
		switch (this.state) {
			case 'loading':
				return 'pi pi-spinner pi-spin';
			case 'matched':
				return 'pi pi-check-circle';
			case 'no-access':
				return 'pi pi-lock';
			case 'no-match':
				return 'pi pi-exclamation-triangle';
			case 'error':
				return 'pi pi-times-circle';
			default:
				return 'pi pi-info-circle';
		}
	}

	private toResult(zipcode: string, data: BranchZipLookupPayload | null): BranchZipLookupResult {
		if (!data?.branch_id) {
			return { zipcode, state: 'no-match', branchId: null, branchName: '', payload: null };
		}

		const branchIdStr = String(data.branch_id);
		const accessible = this.accessibleBranches.some((branch) => String(branch.code) === branchIdStr);

		return {
			zipcode,
			state: accessible ? 'matched' : 'no-access',
			branchId: data.branch_id,
			branchName: data.branch_name || '',
			payload: data
		};
	}

	/**
	 * userBranches is a BehaviorSubject seeded with an empty array, so a ZIP answered before
	 * that list arrives reads as "no access" for a branch the user can in fact pick. Re-run the
	 * verdict whenever the list changes so the panel corrects itself instead of staying wrong.
	 */
	private recheckAccess(): void {
		if (this.state !== 'no-access' || !this.lastPayload || this.queriedZipcode !== this.zipcode) {
			return;
		}

		const result = this.toResult(this.queriedZipcode, this.lastPayload);

		if (result.state !== 'matched' || result.branchId === null) {
			return;
		}

		this.state = result.state;
		this.branchName = result.branchName;
		this.branchResolved.emit({ branchId: result.branchId, branchName: result.branchName });
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
